import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useParams } from "react-router-dom";
import styled from "@emotion/styled/macro";
import { tokens } from "@sunrun/experience-ui-components";
import { Button, LoadingOverlay } from "@sunrun/experience-ui-components";
import { AxiosError } from "axios";
import { useFlags } from "flagsmith/react";
import { getEnvironment } from "@sunrun/sales-experience-shared";
import {
  EditOffer,
  EditOfferProductAvailability,
} from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { ProductSelectionOrganism } from "components/organisms/ProductSelectionOrganism";
// This is an exception as this "page" renders pages.
// eslint-disable-next-line import/no-restricted-paths
import { PricingPage } from "components/pages/PricingPage";
import { DesignOrganism } from "components/organisms/DesignOrganism";
import {
  HeaderButton,
  HeaderContentLayout,
} from "components/templates/HeaderContentLayout";
import { PackageCompareModal } from "components/templates/PackageCompareModal";
import { useAppSelector } from "store";
import {
  reloadMFE,
  useNavigateToBuild,
  useNavigateToChangeOrder,
  useNavigateToList,
  useNavigateToReview,
} from "utils/useNavigateHelper";
import {
  amendOfferWithSync,
  emptyOffer,
  getEditOffer,
  getProducts,
  reconcileRequiredAccessories,
} from "services/storeFront";
import { createOfferInitialize } from "services/storeFront";
import { ErrorComponent } from "components/atoms/ErrorComponent";
import { ErrorModal } from "components/molecules/ErrorModal";
import type { ErrorType } from "components/organisms/ProductSelectionOrganism";
import { publish } from "utils/messages";
import { IhdMessage, logMessage } from "utils/IhdMessage";
import { getIhdBaseUrl } from "components/molecules/InHomeDesignMolecule";
import { sendTrackingEvent } from "services/fullstory";
import { getQueryStringParams } from "utils/queryString";
import {
  NewOfferContext,
  OfferConsumedContext,
} from "providers/NewOfferProvider";
import { NewOfferData } from "providers/Types";
import { FeatureFlags } from "fixtures/features";
import {
  STORAGE_ADDON_BUNDLE_ID,
  STORAGE_ADDON_PRODUCT_ID,
  STANDALONE_BUNDLE_ID,
  STANDALONE_PRODUCT_ID,
  STANDALONE_EV_CHARGER_BUNDLE_ID,
} from "constants/bundleAndProductIds";
import { OPPORTUNITY_STAGES } from "constants/prospect";
import { useUsageData } from "utils/swrHooks";
import { Mixpanel } from "providers/mixpanel";
import { OFFER_CREATION__CLICKED_CONTINUE_ON_PRODUCT_SELECTION_PAGE } from "constants/mixpanelEvents";
import { CognitoUserLabel } from "components/atoms/CognitoUserLabel";
import { hasOnlyIneligibleBundles } from "utils/productAvailability";
import { isNewHomesSalesDivision } from "utils/newHomes";

type Page = "PRODUCT_SELECTION" | "DESIGN" | "PRICING";

const env = getEnvironment();

const getFlagProspectIds = (value: unknown): string[] => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.map(String);
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.map(String);
      }
    } catch {
      return [];
    }
  }

  return [];
};

const CreatePage = ({
  builderOnly,
  changeOrder = false,
}: {
  builderOnly: boolean;
  changeOrder?: boolean;
}) => {
  const [page, setPage] = useState<Page>("PRODUCT_SELECTION");
  const newOffer = useContext(NewOfferContext);
  const offerConsumed = useContext(OfferConsumedContext);
  const {
    prospectId,
    offerId,
    copyDesignOrProposalId: copyDesignOrProposalIdParam,
  } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const skipToPricingFromUrl = searchParams.get("skipToPricing") === "true";
  const copyDesignOrProposalId =
    copyDesignOrProposalIdParam ?? searchParams.get("copyDesignOrProposalId");

  const [productAvailability, setProductAvailability] =
    useState<EditOfferProductAvailability>();
  const [loadingRefetch, setLoadingRefetch] = useState<boolean>(false);
  const [lightmileProjectId, setLightmileProjectId] = useState<string>();
  const [submittingOffer, setSubmittingOffer] = useState<boolean>(false);
  const [selectedPricePointId, setSelectedPricePointId] = useState<string>();
  const [ihdRef, setIhdRef] = useState<
    React.RefObject<HTMLIFrameElement> | undefined
  >();
  const [ihdAppMounted, setIhdAppMounted] = useState(false);
  const pendingGoToDesign = useRef(false);
  const bundleIdCache = useRef<string>();
  const expectedLockVersion = useRef(0);
  const offerLinesIdLookup = useRef<EditOffer["lines"]>([]);
  const currentBundle = useRef(0);
  const previousBundle = useRef(-1);
  const [amplifyApiErrors, setAmplifyApiErrors] = useState<ErrorType>();
  const [showProductErrorModal, setShowProductErrorModal] =
    useState<boolean>(false);
  const isOfferRetry = useRef(false);
  const [offerLineErrors, setOfferLineErrors] = useState<ErrorType>();
  const cognitoToken = useAppSelector(
    (state) => state.auth.cognitoToken?.token
  );
  const identity = useAppSelector((state) => state.auth.identity);
  const [status, setStatus] = useState<EditOffer["status"]>();
  const [showErrorModal, setShowErrorModal] = useState<boolean>();

  const flags = useFlags([
    FeatureFlags.SPAN_AS_ACCESSORY,
    FeatureFlags.NON_SOLAR_IHD_FLOW,
    FeatureFlags.NON_SOLAR_IHD_BACK,
  ]);
  const spanAsAccessoryEnabled = flags[FeatureFlags.SPAN_AS_ACCESSORY]?.enabled;

  const nonSolarIhdFlowFlag = flags[FeatureFlags.NON_SOLAR_IHD_FLOW];
  const isNonSolarIhdFlowFlagEnabled =
    Boolean(nonSolarIhdFlowFlag?.enabled) ||
    Boolean(
      prospectId &&
        getFlagProspectIds(nonSolarIhdFlowFlag?.value).includes(
          prospectId ?? ""
        )
    );

  const nonSolarIhdBackFlag = flags[FeatureFlags.NON_SOLAR_IHD_BACK];
  const isNonSolarIhdBackFlagEnabled =
    Boolean(nonSolarIhdBackFlag?.enabled) ||
    Boolean(
      prospectId &&
        getFlagProspectIds(nonSolarIhdBackFlag?.value).includes(
          prospectId ?? ""
        )
    );

  const { prospect, isLoading: isProspectLoading } = useUsageData(prospectId);

  const isAtClosedWon = prospect?.leadStage === OPPORTUNITY_STAGES.CLOSED_WON;

  const [ihdUnrecoverableError, setIhdUnrecoverableError] = useState<Error>();

  const shouldStayOnDesignAfterIhdError =
    changeOrder || Boolean(copyDesignOrProposalId);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const message = event?.data as IhdMessage;
      if (!message) return;
      if (message.type === "APP_MOUNTED") {
        setIhdAppMounted(true);
      }
    };
    window.addEventListener("message", handler);
    return () => {
      window.removeEventListener("message", handler);
    };
  }, []);

  useEffect(() => {
    if (ihdAppMounted && pendingGoToDesign.current) {
      pendingGoToDesign.current = false;
      const iframe = ihdRef?.current;
      if (iframe) {
        setTimeout(() => {
          const message = {
            source: "Offer Experience",
            type: "IHD_HACK_GO_TO_DESIGN",
          } as IhdMessage;
          logMessage(message);
          iframe.contentWindow?.postMessage(message, getIhdBaseUrl(env));
        }, 100);
      }
    }
  }, [ihdAppMounted, ihdRef]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const message = event?.data as IhdMessage;
      if (!message) return;
      if (message.type === "UNRECOVERABLE_ERROR") {
        const errorMessage =
          message.payload?.error ??
          "An unknown error occurred in In-Home Design leading to an unrecoverable error.";
        setIhdUnrecoverableError(new Error(errorMessage));
        setShowErrorModal(true);
      }
    };
    window.addEventListener("message", handler);
    return () => {
      window.removeEventListener("message", handler);
    };
  }, []);

  const checkProductAvailabilityError = useCallback(
    (productAvailability: EditOfferProductAvailability, offerId: string) => {
      if (hasOnlyIneligibleBundles(productAvailability)) {
        return undefined;
      }

      let error;
      if (
        !productAvailability.bundles ||
        productAvailability.bundles.length === 0
      ) {
        error = new Error("No bundles available for offer " + offerId);
      } else if (
        !productAvailability.products ||
        productAvailability.products.length === 0
      ) {
        error = new Error("No products available for offer " + offerId);
      }
      return error;
    },
    []
  );

  const refetchOffer = useCallback(
    (initialOfferToFetch?: string) => {
      if (!cognitoToken || !identity) {
        return;
      }
      const offerToFetch = offerId ?? initialOfferToFetch;
      if (!offerToFetch) {
        return;
      }
      setLoadingRefetch(true);
      return getEditOffer(cognitoToken, offerToFetch)
        .then((data) => {
          setStatus(data.status);
          setLightmileProjectId(data?.lightmileProjectId);
          if (data.lines.length > 0 && data.bundleId) {
            if (!isOfferRetry.current) {
              bundleIdCache.current = data.bundleId;
              offerLinesIdLookup.current = data.lines;
            }
          }
          const refetchedOfferBundleId = data.bundleId;
          expectedLockVersion.current = data.lockVersion;

          const ePermitting =
            getQueryStringParams()?.ePermitting?.toString() === "true";
          return getProducts(
            cognitoToken,
            offerToFetch,
            data.opportunity && data.opportunity.state,
            data.opportunity && data.opportunity.acpMarketType,
            ePermitting,
            identity
          )
            .then((data) => {
              let error = checkProductAvailabilityError(data, offerToFetch);
              if (error) {
                setAmplifyApiErrors({
                  error,
                  attemptedAction: "creating a new Offer",
                });
                return;
              }
              const refetchedOfferBundle = data.bundles.find(
                (bundle) => bundle.id === refetchedOfferBundleId
              );
              const isOriginalStorageAddon =
                refetchedOfferBundle?.productIds?.includes(
                  STORAGE_ADDON_PRODUCT_ID
                ) ?? false;
              const isOriginalStandalone =
                refetchedOfferBundle?.productIds?.includes(
                  STANDALONE_PRODUCT_ID
                ) ?? false;
              const isOriginalStandaloneEvChargerBundle =
                refetchedOfferBundleId === STANDALONE_EV_CHARGER_BUNDLE_ID;
              if (!isOriginalStorageAddon && changeOrder) {
                data.bundles = data?.bundles?.filter((bundle) => {
                  return !bundle?.productIds?.includes(
                    STORAGE_ADDON_PRODUCT_ID
                  );
                });
                data.products = data?.products?.filter((product) => {
                  return product?.id !== STORAGE_ADDON_PRODUCT_ID;
                });
              }
              if (!isOriginalStandalone && changeOrder) {
                data.bundles = data?.bundles?.filter((bundle) => {
                  return !bundle?.productIds?.includes(STANDALONE_PRODUCT_ID);
                });
                data.products = data?.products?.filter((product) => {
                  return product?.id !== STANDALONE_PRODUCT_ID;
                });
              }
              if (!isOriginalStandaloneEvChargerBundle && changeOrder) {
                data.bundles = data?.bundles?.filter((bundle) => {
                  return bundle?.id !== STANDALONE_EV_CHARGER_BUNDLE_ID;
                });
              }
              // Self-heal offers that pre-date the bundle accessory metadata:
              // create any missing required-accessory lines so the UI doesn't
              // try to update non-existent lines on first interaction. Only
              // runs under the SPAN-as-accessory flag; legacy offers never
              // had required-accessory lines to reconcile.
              if (!spanAsAccessoryEnabled) {
                setProductAvailability(data);
                return;
              }
              const activeBundle =
                data.bundles.find((b) => b.id === refetchedOfferBundleId) ??
                data.bundles.find((b) => b.id === bundleIdCache.current);
              if (!activeBundle) {
                setProductAvailability(data);
                return;
              }
              return reconcileRequiredAccessories(
                cognitoToken,
                offerToFetch,
                activeBundle,
                data.products,
                offerLinesIdLookup.current,
                expectedLockVersion.current
              )
                .then((reconciled) => {
                  if (reconciled) {
                    offerLinesIdLookup.current = reconciled.lines;
                    expectedLockVersion.current = reconciled.lockVersion;
                  }
                })
                .catch((error) => {
                  // Non-fatal: if reconciliation fails, the UI's required-mode
                  // ProductAccessory will still issue an `add` on first render.
                  console.error(
                    "Failed to reconcile required accessories",
                    error
                  );
                })
                .then(() => {
                  setProductAvailability(data);
                });
            })
            .catch((error) => {
              console.error(error);
              setAmplifyApiErrors({
                error,
                attemptedAction: "fetching the list of products",
              });
            });
        })
        .catch((error) => {
          console.error(error);
          setAmplifyApiErrors({
            error,
            attemptedAction: "fetching the existing Offer",
          });
        })
        .then(() => {
          setLoadingRefetch(false);
        });
    },
    [
      checkProductAvailabilityError,
      cognitoToken,
      identity,
      offerId,
      changeOrder,
      spanAsAccessoryEnabled,
    ]
  );
  const goToProductSelection = useCallback(() => {
    setPage("PRODUCT_SELECTION");
    refetchOffer();
  }, [setPage, refetchOffer]);
  const openProductErrorModal = useCallback(
    () => setShowProductErrorModal(true),
    [setShowProductErrorModal]
  );
  const closeProductErrorModal = useCallback(() => {
    setShowProductErrorModal(false);
    goToProductSelection();
  }, [setShowProductErrorModal, goToProductSelection]);
  const retryAfterProductErrorModalClose = useCallback(() => {
    isOfferRetry.current = true;
    closeProductErrorModal();
  }, [closeProductErrorModal, isOfferRetry]);

  const selectedBundleId =
    productAvailability?.bundles[currentBundle.current]?.id;
  const isNonSolarNewOffer =
    !changeOrder &&
    (selectedBundleId === STORAGE_ADDON_BUNDLE_ID ||
      selectedBundleId === STANDALONE_BUNDLE_ID ||
      selectedBundleId === STANDALONE_EV_CHARGER_BUNDLE_ID);
  const isNonSolarIhdFlowEligible =
    !changeOrder &&
    (selectedBundleId === STORAGE_ADDON_BUNDLE_ID ||
      selectedBundleId === STANDALONE_BUNDLE_ID);
  const useNonSolarIhdFlow =
    isNonSolarIhdFlowEligible && isNonSolarIhdFlowFlagEnabled;
  const useNonSolarIhdBack = useNonSolarIhdFlow && isNonSolarIhdBackFlagEnabled;
  const hideIhdBehindLoadingOverlay =
    !useNonSolarIhdFlow && (isNonSolarNewOffer || skipToPricingFromUrl);
  const goToDesign = useCallback(
    (allowSkipMessage = false) => {
      if (hasOnlyIneligibleBundles(productAvailability)) {
        return;
      }

      Mixpanel.track(
        OFFER_CREATION__CLICKED_CONTINUE_ON_PRODUCT_SELECTION_PAGE,
        {
          offerId,
          bundleId: productAvailability?.bundles[currentBundle.current]?.id,
          bundleTitle:
            productAvailability?.bundles[currentBundle.current]?.title,
          bundleDescription:
            productAvailability?.bundles[currentBundle.current]?.description,
        }
      );
      const iframe = ihdRef?.current;
      if (!iframe && !allowSkipMessage) {
        return;
      }
      if (iframe) {
        if (ihdAppMounted) {
          setTimeout(() => {
            const message = {
              source: "Offer Experience",
              type: "IHD_HACK_GO_TO_DESIGN",
            } as IhdMessage;
            logMessage(message);
            iframe.contentWindow?.postMessage(message, getIhdBaseUrl(env));
          }, 100);
        } else {
          pendingGoToDesign.current = true;
        }
      }
      setPage("DESIGN");
    },
    [ihdRef, ihdAppMounted, offerId, productAvailability, currentBundle]
  );
  const goToPricing = useCallback(() => {
    setPage("PRICING");
    refetchOffer();
  }, [setPage, refetchOffer]);
  const goToList = useNavigateToList();
  const goBackFromPricing = useCallback(() => {
    if (skipToPricingFromUrl) {
      goToList({ skipToPricing: false });
      return;
    }
    if (useNonSolarIhdBack) {
      goToDesign();
      return;
    }
    if (isNonSolarNewOffer) {
      goToProductSelection();
      return;
    }
    goToDesign();
  }, [
    skipToPricingFromUrl,
    useNonSolarIhdBack,
    isNonSolarNewOffer,
    goToList,
    goToDesign,
    goToProductSelection,
  ]);
  const goToBuild = useNavigateToBuild({ replace: true });
  const goToChangeOrder = useNavigateToChangeOrder({ replace: true });
  const goToReview = useNavigateToReview({ replace: true });
  const hasRunCreateOffer = useRef(false);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState<boolean>(false);
  const openPackageCompareModal = useCallback(
    () => setIsCompareModalOpen(true),
    [setIsCompareModalOpen]
  );
  const closePackageCompareModal = useCallback(
    () => setIsCompareModalOpen(false),
    [setIsCompareModalOpen]
  );
  const [isPrimaryActionButtonDisabled, setIsPrimaryActionButtonDisabled] =
    useState<boolean>();
  const handlePrimaryButtonDisable = useCallback(
    (isLoading: boolean) => setIsPrimaryActionButtonDisabled(isLoading),
    [setIsPrimaryActionButtonDisabled]
  );

  const isNewHomes = isNewHomesSalesDivision(prospect?.salesDivision);
  const disableOfferSubmit = useMemo(() => {
    return (
      !selectedPricePointId ||
      submittingOffer ||
      (!builderOnly && !isNewHomes && (isAtClosedWon || isProspectLoading))
    );
  }, [
    selectedPricePointId,
    submittingOffer,
    builderOnly,
    isAtClosedWon,
    isProspectLoading,
    isNewHomes,
  ]);

  useEffect(() => {
    if (hasRunCreateOffer.current) return;
    if (!cognitoToken || !identity) return;
    // Defer until Flagsmith has resolved the SPAN_AS_ACCESSORY flag so the
    // one-shot offer-init / refetch picks the correct (new vs legacy) path
    // and the self-heal in reconcileRequiredAccessories isn't permanently
    // skipped. The effect re-runs when `spanAsAccessoryEnabled` flips from
    // undefined to a boolean (already in deps).
    if (spanAsAccessoryEnabled === undefined) return;
    if (offerId) {
      refetchOffer()?.then(() => {
        if (changeOrder || copyDesignOrProposalId) {
          goToDesign(true);
        }
        sendTrackingEvent("New Offer Ready", { source: "Refetch" });
      });
      hasRunCreateOffer.current = true;
      return;
    }
    if (changeOrder) {
      const parentProposalId =
        getQueryStringParams()?.parentProposalId?.toString();
      const unsignedSourceProposalId =
        getQueryStringParams()?.unsignedSourceProposalId?.toString();
      const signedRootId = getQueryStringParams()?.signedRootId?.toString();
      if (parentProposalId && signedRootId) {
        amendOfferWithSync(
          cognitoToken,
          prospectId!,
          parentProposalId,
          signedRootId
        )
          .then((result) => {
            goToChangeOrder(result);
            refetchOffer(result)?.then(() => {
              goToDesign(true);
              sendTrackingEvent("New Offer Ready", { source: "Initialize" });
            });
          })
          .catch((error) => {
            console.error(error);
            setAmplifyApiErrors({
              error,
              attemptedAction: "starting a change order",
            });
          });
      } else if (unsignedSourceProposalId && !signedRootId) {
        emptyOffer(
          cognitoToken,
          prospectId!,
          undefined,
          unsignedSourceProposalId
        )
          .then((result) => {
            goToChangeOrder(result.id);
            refetchOffer(result.id)?.then(() => {
              goToDesign(true);
              sendTrackingEvent("New Offer Ready", {
                source: "UnsignedSourceProposal",
              });
            });
          })
          .catch((error) => {
            console.error(error);
            setAmplifyApiErrors({
              error,
              attemptedAction:
                "starting an offer from unsigned source proposal",
            });
          });
      } else {
        setAmplifyApiErrors({
          error: new Error(
            "Change order requires parentProposalId and signedRootId (signed), or unsignedSourceProposalId without signedRootId (unsigned empty offer)."
          ),
          attemptedAction: "starting a change order",
        });
        return;
      }
    } else {
      if (!newOffer) {
        // Loading is still in progress
        return;
      }
      if (!(newOffer as NewOfferData).offerId) {
        setAmplifyApiErrors({
          error: newOffer as AxiosError,
          attemptedAction: "starting a new offer",
        });
        return;
      }
      createOfferInitialize(
        cognitoToken,
        newOffer as NewOfferData,
        spanAsAccessoryEnabled
      )
        .then((result) => {
          offerConsumed();
          if (!result.offer) {
            const error = checkProductAvailabilityError(
              result.products,
              result.offerId
            );
            if (error) {
              setAmplifyApiErrors({
                error,
                attemptedAction: "creating a new Offer",
              });
              return;
            }
            setProductAvailability(result.products);
            goToBuild(result.offerId, undefined);
            sendTrackingEvent("New Offer Ready", { source: "Initialize" });
            return;
          }
          expectedLockVersion.current = result.offer.lockVersion;
          bundleIdCache.current = result.offer.bundleId;
          if (result.offer.lines.length > 0 && result.offer.bundleId) {
            if (!isOfferRetry.current) {
              bundleIdCache.current = result.offer.bundleId;
              offerLinesIdLookup.current = result.offer.lines;
            }
          }
          setProductAvailability(result.products);
          goToBuild(result.offer.id, undefined);
          sendTrackingEvent("New Offer Ready", { source: "Initialize" });
        })
        .catch((error) => {
          if (error?.response?.statusCode === 409) {
            // The empty offer was used by another user, so we need to reload the page
            reloadMFE();
            return;
          }
          console.error(error);
          setAmplifyApiErrors({
            error,
            attemptedAction: "creating a new Offer",
          });
        });
    }
    hasRunCreateOffer.current = true;
  }, [
    cognitoToken,
    offerId,
    prospectId,
    goToBuild,
    refetchOffer,
    checkProductAvailabilityError,
    changeOrder,
    copyDesignOrProposalId,
    goToDesign,
    goToChangeOrder,
    newOffer,
    offerConsumed,
    identity,
    spanAsAccessoryEnabled,
  ]);

  const onlyIneligibleBundles = hasOnlyIneligibleBundles(productAvailability);

  const productHeaderButtons = useMemo(() => {
    const buttons: HeaderButton[] = [
      {
        type: "primary",
        text: "Continue",
        disabled: isPrimaryActionButtonDisabled || onlyIneligibleBundles,
        onClick: goToDesign,
        dataTestId: "go-to-design-btn",
      },
    ];
    if (!builderOnly) {
      buttons.unshift({
        type: "secondary",
        text: "Back",
        disabled: submittingOffer,
        onClick: goToList,
      });
    }
    return buttons;
  }, [
    builderOnly,
    goToDesign,
    goToList,
    isPrimaryActionButtonDisabled,
    onlyIneligibleBundles,
    submittingOffer,
  ]);

  if (amplifyApiErrors) {
    return (
      <HeaderContentLayout
        content={
          <ErrorComponent
            context="CreatePage"
            action={amplifyApiErrors.attemptedAction}
            error={amplifyApiErrors.error}
          />
        }
        title="There was a Problem."
        description={`Something went wrong ${amplifyApiErrors.attemptedAction}. Please try refreshing the page.`}
      />
    );
  }

  if (
    !(
      ["DRAFT", "SUBMIT_PENDING", "SUBMIT_FAILED", undefined] as Array<
        EditOffer["status"] | undefined
      >
    ).includes(status)
  ) {
    return (
      <HeaderContentLayout
        content={
          <StyleOfferUneditable>
            <em className="banner">{`This Offer cannot be further edited. Offer status is "${status}".`}</em>
            {!builderOnly && (
              <div className="actions">
                <Button
                  data-testid={"go-to-review-btn"}
                  onClick={() => goToReview(offerId)}
                >
                  Go to offer
                </Button>
                <Button onClick={() => goToList()}>Go to list</Button>
              </div>
            )}
          </StyleOfferUneditable>
        }
        title="Build a bundle."
        kicker="Offer Builder"
        buttons={
          builderOnly
            ? []
            : [
                {
                  type: "secondary",
                  text: "Back",
                  onClick: goToList,
                },
              ]
        }
      />
    );
  }
  if (!offerId || !productAvailability) {
    return (
      <HeaderContentLayout
        content={
          <LoadingOverlay
            message="Loading..."
            data-testid="create-page-loading-overlay"
          />
        }
        title="Build a bundle."
        kicker="Offer Builder"
        description="Make it yours. Assemble the package that best fits your needs."
        buttons={
          builderOnly
            ? []
            : [
                {
                  type: "secondary",
                  text: "Back",
                  disabled: submittingOffer,
                  onClick: goToList,
                },
              ]
        }
      />
    );
  }
  return (
    <>
      {loadingRefetch && (
        <LoadingContainer data-testid="loading-refetch-overlay">
          <LoadingOverlay
            message="Loading..."
            data-testid="create-page-loading"
          />
        </LoadingContainer>
      )}
      {ihdUnrecoverableError &&
        showErrorModal &&
        page !== "PRODUCT_SELECTION" && (
          <ErrorModal
            title="Error from another system"
            context="IHD"
            error={ihdUnrecoverableError}
            errorFromAnotherSystem={true}
            severity="warning"
            {...(shouldStayOnDesignAfterIhdError
              ? { onClose: () => setShowErrorModal(false) }
              : {
                  onRetry: () => {
                    setShowErrorModal(false);
                    goToProductSelection();
                  },
                })}
          />
        )}
      {page === "PRODUCT_SELECTION" && (
        <>
          <HeaderContentLayout
            content={
              <ProductSelectionOrganism
                offerId={offerId}
                productAvailability={productAvailability}
                onBundleLearnMore={openPackageCompareModal}
                bundleIdCache={bundleIdCache}
                expectedLockVersion={expectedLockVersion}
                offerLinesIdLookup={offerLinesIdLookup}
                onIsLoading={handlePrimaryButtonDisable}
                openProductErrorModal={openProductErrorModal}
                setOfferLineErrors={setOfferLineErrors}
                isOfferRetry={isOfferRetry}
                currentBundle={currentBundle}
                previousBundle={previousBundle}
              />
            }
            title="Build a bundle"
            description={
              <>
                Make it yours. Assemble the package that best fits your needs.
                <CognitoUserLabel />
              </>
            }
            buttons={productHeaderButtons}
            kicker="Offer Builder"
          />

          {isCompareModalOpen && (
            <PackageCompareModal
              modalTitle="Bundle Options"
              modalDescription="Sunrun offers product bundles with a range of capabilities so you can find the solar package that is right for your needs. Compare our options below."
              productAvailability={productAvailability}
              onClose={closePackageCompareModal}
            />
          )}
          {showProductErrorModal && (
            <ErrorModal
              action={offerLineErrors?.attemptedAction}
              error={offerLineErrors?.error}
              context="CreatePage"
              onClose={closeProductErrorModal}
              onRetry={
                offerLineErrors?.hasRetry
                  ? retryAfterProductErrorModalClose
                  : undefined
              }
            />
          )}
        </>
      )}
      {page === "PRICING" && (
        <div>
          <HeaderContentLayout
            smallPadding
            content={
              <PricingPage
                lightmileProjectId={lightmileProjectId!}
                offerId={offerId}
                prospectId={prospectId!}
                changeOrder={changeOrder}
                offerEvSpan={productAvailability.offerEvSpan}
                submittingOffer={submittingOffer}
                onPricePointIdSelected={(pricePointId) =>
                  setSelectedPricePointId(pricePointId)
                }
                onSubmitFailure={() => setSubmittingOffer(false)}
                onSubmitComplete={() => {
                  if (!builderOnly) {
                    goToReview(offerId);
                  }
                  publish.finished();
                }}
              />
            }
            title="Pricing"
            description={<CognitoUserLabel />}
            buttons={[
              {
                type: "secondary",
                text: "Back",
                disabled: submittingOffer,
                onClick: goBackFromPricing,
              },
              {
                type: "primary",
                text: builderOnly ? "Finish" : "Go to Review",
                disabled: disableOfferSubmit,
                onClick: () => setSubmittingOffer(true),
              },
            ]}
          />
        </div>
      )}
      <div
        data-testid="design-container"
        style={{
          display: page === "DESIGN" ? "block" : "none",
        }}
      >
        {hideIhdBehindLoadingOverlay && (
          <LoadingContainer data-testid="non-solar-battery-ihd-loading-spinner">
            <LoadingOverlay
              message="Loading..."
              data-testid="non-solar-battery-ihd-loading-overlay"
            />
          </LoadingContainer>
        )}
        <DesignOrganism
          offerId={offerId}
          prospectId={prospectId!}
          copyDesignOrProposalId={copyDesignOrProposalId ?? undefined}
          skipToPricing={skipToPricingFromUrl && !useNonSolarIhdFlow}
          goToProductSelection={goToProductSelection}
          goToPricing={goToPricing}
          setIhdRef={setIhdRef}
        />
      </div>
    </>
  );
};

const StyleOfferUneditable = styled.div`
  .banner {
    display: block;
    font-size: 1.25em;
    margin: 1em 0;
  }
  .actions {
    display: flex;
    gap: 1em;
  }
`;

const LoadingContainer = styled.div`
  position: fixed;
  top: 0px;
  left: 0px;
  width: 100vw;
  height: 100vh;
  z-index: 1000;

  background: linear-gradient(
    180deg,
    ${tokens.CHARGEBLUE_50} 0,
    ${tokens.BACKGROUND_LIGHT} 16rem
  );
`;

export { CreatePage };
